/* eslint-disable complexity */
import { type MouseEvent, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, styled } from "~/common/mui";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { defaultSelectBestPackage, PSSizeMode } from "~/utils/cartHelper";
import { Alert } from "~/common/mui/Alert/Alert";
import { LinkType } from "~/common/components/Link";
import CenterContents from "~/common/components/CenterContents";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { isKITProduct, type ProductData } from "~/domains/product/types/ProductData";
import { Product } from "~/domains/product/Product";
import { ProductContext } from "~/domains/product/ProductContext";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { usePurchasePlace, usePurchaseType } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { makeInitialOptionalPartsDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { calculateSalesCampaign4PDP, useSelectPricingDetails } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { SaleQuantityIndicator } from "~/domains/salesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";

import { ProductComparisonCheckbox } from "~/features/compareProducts/components/ProductComparisonCheckbox";
import { isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import { BackInStockIndicator } from "~/domains/product/components/StockIndicator/StockIndicator";
import { ProductRecoType } from "~/features/saleRecommendations/components/ProductRecommendationSection";
import { PriceElements, type PriceProps } from "~/domains/pricing/components/Price/PriceV4";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { renderButtons } from "~/features/saleRecommendations/hooks/useRecommendationTileButtons";
import { useAddProductToWishlistFlow } from "~/features/wishlist/hooks/useAddProductToWishlistFlow";

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface trackingAttributesProps {
  rootTrackingAttributes?: any;
  linkProps: any;
  buttonProps: any;
  wishListBtnProps?: any;
  gaLocation?: string;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

interface ProductTileProps {
  productCode: string;
  squeezePrice?: boolean;
  itemWidth: number;
  variant: string;
  getTrackingAttributes: (
    product: ProductData,
    productDescriptor: ProductDescriptor,
    isPMA: boolean,
  ) => trackingAttributesProps;
}

export const ProductRecommendationsTile = ({
  productCode,
  itemWidth,
  variant,
  getTrackingAttributes,
}: ProductTileProps) => {
  const { t } = useTranslation();
  const reco_type = usePurchaseType();
  const reco_place = usePurchasePlace();

  const isRecentlyViewedReco = variant === ProductRecoType.recentlyViewed;
  const isTopSeller = variant === ProductRecoType.topSeller;

  const pnaCallEnabled = !window.SCHA.feature.recoPrice;

  const priceReference = getInitialPriceReference();
  const productDescriptor = ProductService.useProductDescriptor({
    productCode: productCode,
    priceReference,
    options: {
      fetch: true,
      fetchAvailability: pnaCallEnabled && !isTopSeller,
      fetchPrice: pnaCallEnabled,
    },
  });
  const product = productDescriptor.base;

  const isPMA = isProductPMA(productDescriptor);

  const {
    rootTrackingAttributes,
    linkProps,
    buttonProps,
    wishListBtnProps,
    gaLocation = "",
  } = getTrackingAttributes(product, productDescriptor, isPMA);

  const packaging = defaultSelectBestPackage(
    1,
    productDescriptor.availability?.packagingInformation?.map((p) => p.packCode) ?? [],
    productDescriptor,
    PSSizeMode.SMALLEST_FIRST,
  );
  const packCode = packaging?.packCode ?? INVALID_PACKAGE_CODE;

  const salesHelper = useSalesHelper(productDescriptor);
  const isOnSale = salesHelper.isPackageOnSale(packCode);
  const saleStock = salesHelper.forPackage(packCode)?.stock ?? 0;
  const { data } = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor }), [productDescriptor]);
  const { netPrice, price, oldPrice, discountFormatted } = useSelectPricingDetails({
    packCode,
    quantity: 1,
    priceReference: getInitialPriceReference(),
    productCode: productCode,
    salesData: data?.salePerPack?.[packCode],
    saleActivated: isOnSale,
  });

  let priceElement: ReactNode;
  if (!userHasPermission(Permission.PRICE) || !pnaCallEnabled) {
    priceElement = null;
  } else if (productDescriptor.isPriceError) {
    priceElement = <Alert severity={"error"}>{"Error loading data"}</Alert>;
  } else if (productDescriptor.isPriceLoading) {
    priceElement = (
      <CenterContents>
        <CircleSpinner />
      </CenterContents>
    );
  } else {
    const tariffSurcharge = isTariffSurchargeEnabled() && netPrice?.tariffSurcharge;
    priceElement = (
      <PriceElements.Stack>
        <Stack direction="row" gap={1}>
          <CurrentPrice
            price={price}
            zeroText={t("product.priceOnRequest")}
            tariffSurcharge={tariffSurcharge}
            variant="headlineSmall"
          />
          <SaleDiscountIndicator.V2 discountFormatted={discountFormatted} />
        </Stack>
        {oldPrice && <OldPrice price={oldPrice} />}
        <PriceElements.NetPriceLabel price={netPrice} />
        {tariffSurcharge && <p className="label-small">{t("price.tariff.surcharge.applicable")}</p>}
      </PriceElements.Stack>
    );
  }

  const onAddToCartClick = useMemo(() => {
    return async (_: MouseEvent<HTMLButtonElement>) => {
      let finalInitialData = undefined;
      if (isRecentlyViewedReco) {
        const isHousingKit = isKITProduct(product);
        const optionalParts = (isHousingKit && product.optionalParts) || [];
        const selectedPack = productDescriptor.availability?.packagingInformation[0];
        finalInitialData = isHousingKit
          ? {
              quantity: selectedPack?.packSize ?? undefined,
              priceReference: getInitialPriceReference(),
              selectedPackCode: selectedPack?.packCode ?? undefined,
              optionalParts: makeInitialOptionalPartsDraft(optionalParts, 1),
            }
          : undefined;
      }

      await AddToCartService.openAddToCartWizard(
        {
          productCode: product.code,
          restrictionQuery: "",
          initialData: finalInitialData,
        },
        { page: reco_place, recommendationType: reco_type, recommendationPlace: reco_place },
      );
    };
  }, [reco_place, reco_type, product, productDescriptor, isRecentlyViewedReco]);

  const [openWishlistModal, wishlistModal] = useAddProductToWishlistFlow({
    productInput: product,
  });

  const buttonElement = renderButtons(productDescriptor, {
    atc: {
      onClick: onAddToCartClick,
      disabled: productDescriptor.isLoading || productDescriptor.isError || productDescriptor.isPriceLoading,
      ...buttonProps,
    },
    wishlist: {
      onClick: () => openWishlistModal(),
      ...wishListBtnProps,
    },
  })[0];

  return (
    <>
      {wishlistModal}

      <ProductContext value={product}>
        <TileRoot sx={{ width: itemWidth }}>
          {isOnSale && <SaleTag sx={{ position: "absolute", left: 0, top: 0, zIndex: 2 }} />}
          <TileCard {...rootTrackingAttributes}>
            <Stack direction="row" sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
              <Box
                sx={{
                  width: 115,
                  height: 115,
                  pl: "10px",
                  "& img": { width: "100%", height: "100%", objectFit: "contain" },
                }}
              >
                <Product.PrimaryImage linkProps={linkProps} />
              </Box>
              {isRecentlyViewedReco && product.isProductBackInStock && <BackInStockIndicator />}
            </Stack>

            <Stack sx={{ gap: 0 }}>
              <Product.DesignationContainer>
                <Product.PreferredIndicator />
                <Product.PdpLink
                  reco_type={reco_type}
                  {...linkProps}
                  variant={LinkType.NAVIGATION}
                  sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  <Product.DesignationV2
                    component={"span"}
                    variant={"titleLarge"}
                    sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  />
                </Product.PdpLink>
              </Product.DesignationContainer>

              <Product.PdpLink reco_type={reco_type} {...linkProps}>
                <Product.ShortDescriptionV2 component={"span"} />
              </Product.PdpLink>
            </Stack>

            <Stack gap={2} sx={{ mb: "auto" }}>
              {priceElement}
              {isOnSale && pnaCallEnabled && <SaleQuantityIndicator.V2 quantity={saleStock} />}
            </Stack>

            {window.SCHA.feature.recoAvailability && <Product.StockIndicator />}

            <ProductComparisonCheckbox gaLocation={gaLocation} productCode={product.code} />

            {buttonElement}
          </TileCard>
        </TileRoot>
      </ProductContext>
    </>
  );
};

const TileRoot = styled("div")(({ theme }) => ({
  containerType: "inline-size",
  height: "100%",
  background: theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  position: "relative",
}));

const TileCard = styled("div")({
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  height: "100%",
});

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.BodySmall),
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));
